import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
    Modal,
    Button,
    Card,
    CardHeader,
    CardBody,
    CardTitle,
    Spinner,
} from 'reactstrap';
import { Field, Form } from 'react-final-form';
import axios from 'axios';
import { useDispatch, flash, flashSuccess, flashError } from '@/shared/components/flash';
import { ThemeProps, RTLProps } from '@/shared/prop-types/ReducerProps';
import { toCurrency, isFailedResponse } from '@/shared/helpers';
import { userName } from './helpers';
import FullPageLoader from '@/shared/components/FullPageLoader';

// Overrides .alert__content's 10px 40px 10px 20px so the alerts and the paragraph line up.
const CONTENT_PADDING = { padding: '10px' };

const validate = (values) => {
    const errors = {};
    if (!values.review_note || values.review_note.trim().length < 3) {
        errors.review_note = 'A review note is required (minimum 3 characters).';
    }
    return errors;
};

const MarkSafeModal = ({ organization, user, hold, pendingHolds, redemptionValue, isOpen, toggle, onSuccess, theme, rtl }) => {
    const dispatch = useDispatch();
    // Holds from this account's other events, e.g. when marking safe from a Watch event.
    const otherPendingHolds = (pendingHolds || []).filter(h => h.id !== hold?.id);

    const onSubmit = (values) => {
        return axios.put(
            `/organization/${organization.id}/user/${user.id}/redemption-review/mark-safe`,
            { review_note: values.review_note }
        )
            .then((response) => {
                // See view.jsx's confirmApprove() for why a resolved promise
                // isn't necessarily success: the app-wide axios interceptor
                // resolves error responses too, after flashing its own
                // message, so business-logic failures must be detected here.
                if (isFailedResponse(response)) return;
                const pendingHolds = response.data.pending_holds || [];
                if (pendingHolds.length > 0) {
                    flash('warning', dispatch, `Redeemer marked safe. ${pendingHolds.length} held redemption${pendingHolds.length === 1 ? ' is' : 's are'} still pending (hold #${pendingHolds.map(h => h.id).join(', #')}) - approve or decline ${pendingHolds.length === 1 ? 'it' : 'them'} separately.`);
                } else {
                    flashSuccess(dispatch, 'Redeemer marked safe. Redemption review cleared for this account.');
                }
                toggle();
                if (onSuccess) onSuccess();
            })
            .catch(error => {
                flashError(dispatch, error.response?.data);
            });
    };

    return (
        <Modal className={`modal-action modal-md ${theme?.className || ''} ${rtl?.direction || 'ltr'}-support`} isOpen={isOpen} toggle={toggle}>
            <Card className="w-100">
                <CardHeader tag="h3">
                    Mark Redeemer Safe
                    <Button className="btn float-end" close onClick={toggle} />
                </CardHeader>
                <CardBody>
                    <CardTitle className="text-left">
                        {userName(user)}
                    </CardTitle>

                    {hold && ['PENDING', 'PROCESSING'].includes(hold.status) && (
                        <div className="alert alert-warning">
                            <div className="alert__content" style={CONTENT_PADDING}>
                                This account has a pending held redemption of {redemptionValue ? toCurrency(redemptionValue) : ''}.
                                Marking safe will <strong>not</strong> release it — use Approve/Decline separately to process that redemption.
                            </div>
                        </div>
                    )}

                    {otherPendingHolds.length > 0 && (
                        <div className="alert alert-warning">
                            <div className="alert__content" style={CONTENT_PADDING}>
                                This account has {otherPendingHolds.length === 1 ? 'a held redemption' : `${otherPendingHolds.length} held redemptions`} still pending or being processed from
                                another event (hold #{otherPendingHolds.map(h => h.id).join(', #')}).
                                Marking safe will <strong>not</strong> release {otherPendingHolds.length === 1 ? 'it' : 'them'} — approve or decline {otherPendingHolds.length === 1 ? 'it' : 'them'} separately.
                            </div>
                        </div>
                    )}

                    <p className="text-muted alert__content" style={CONTENT_PADDING}>
                        This clears the account&apos;s persistent redemption-review state so future redemption
                        attempts proceed normally. It does not change the historical risk score or classification,
                        and future redemptions still run through the normal risk engine.
                    </p>

                    <Form
                        onSubmit={onSubmit}
                        validate={validate}
                    >
                        {({ handleSubmit, submitting }) => (
                            <form className="form" onSubmit={handleSubmit}>
                                <FullPageLoader show={submitting} text="Marking redeemer safe..." />
                                <Field name="review_note">
                                    {({ input, meta }) => (
                                        <div className="form__form-group">
                                            <span className="form__form-group-label">Review Note (required):</span>
                                            <div className="form__form-group-field">
                                                <div className="form__form-group-row">
                                                    <textarea rows={3} {...input} />
                                                </div>
                                            </div>
                                            {meta.touched && meta.error && (
                                                <span className="form__form-group-error">{meta.error}</span>
                                            )}
                                        </div>
                                    )}
                                </Field>
                                <div className="row d-flex justify-content-end">
                                    <Button className="btn-sm mr-2" color="primary" type="submit" disabled={submitting}>
                                        {submitting ? <>Processing <Spinner size="sm" className="ms-1 align-middle" /></> : 'Mark Redeemer Safe'}
                                    </Button>
                                    <Button className="btn-sm" color="outline-primary" onClick={toggle} disabled={submitting}>Cancel</Button>
                                </div>
                            </form>
                        )}
                    </Form>
                </CardBody>
            </Card>
        </Modal>
    );
};

MarkSafeModal.propTypes = {
    theme: ThemeProps.isRequired,
    rtl: RTLProps.isRequired,
};

export default withRouter(connect((state) => ({
    theme: state.theme,
    rtl: state.rtl,
    organization: state.organization,
}))(MarkSafeModal));
